import {
  Box,
  CircularProgress,
  IconButton,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  DirectionsCar,
  Email,
  Home,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
  Person,
  Phone,
} from '@mui/icons-material';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BrandLogoChat } from 'components/DeepSearch/components/BrandLogoChat';
import { ChatChips } from 'components/DeepSearch/components/ChatChips';
import { ChatTerms } from 'components/DeepSearch/components/ChatTerms/ChatTerms';
import {
  PLACEHOLDER_COLLECTION,
  SEARCH_TYPES,
} from 'components/DeepSearch/constants';
import { ChipData } from 'components/DeepSearch/types/chatTypes';

import { DeepSearchContext } from '../context';

import { A2uiReportRenderer } from './A2uiReportRenderer';
import {
  DeepSearchChatInput,
  DeepSearchChatInputRef,
} from './DeepSearchChatInput';
import {
  ChatViewInputWrapper,
  DeepSearchLandingPageContainer,
  FadeOutBox,
  LandingContentInnerStack,
  LandingContentOuterStack,
  LogoTextGroupStack,
  MessagesArea,
  MessageBubble,
  PageContainer,
  ShimmerText,
  SlideOutChipsStack,
  SlideOutInputWrapper,
  SlideOutTermsStack,
  ThinkingStepsIndicator,
  TitleHighlight,
  TitlePhrase,
  TitleRow,
  buildLogoAnimationSx,
} from './DeepSearchPage.styles';
import { FilePreview } from './FileUpload';
import { MarkdownMessage } from './MarkdownMessage';

const CHIPS: ChipData[] = [
  {
    icon: <Person sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'Find a person',
    type: SEARCH_TYPES.PERSON,
  },
  {
    icon: <Phone sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'Search a phone',
    type: SEARCH_TYPES.PHONE,
  },
  {
    icon: <Home sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'View address info',
    type: SEARCH_TYPES.PROPERTY,
  },
  {
    icon: <Email sx={{ color: 'primary.main', fontSize: '16px' }} />,
    title: 'Check an email',
    type: SEARCH_TYPES.EMAIL,
  },
  {
    icon: <DirectionsCar sx={{ color: 'primary.main', fontSize: '20px' }} />,
    title: 'Search a vehicle',
    type: SEARCH_TYPES.VEHICLE,
  },
];

export const DeepSearchPage = () => {
  const { contextId } = useParams<{ contextId?: string }>();
  const {
    activeMessages: messages,
    activeConversationId,
    conversations,
    isLoading,
    isLoadingSession,
    thinkingStep,
    selectConversation,
  } = useContext(DeepSearchContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const lastMessageTopRef = useRef<HTMLDivElement>(null);
  const prevIsLoadingSession = useRef(false);
  const userMsgRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const [canGoPrev, setCanGoPrev] = useState(false);
  const [canGoNext, setCanGoNext] = useState(false);
  const [atTop, setAtTop] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const chatInputRef = useRef<DeepSearchChatInputRef>(null);
  const { isMobile } = useScreenSize();

  const pageContainerRef = useRef<HTMLDivElement>(null);
  // Holds the in-flight animation setTimeouts so they can be cancelled on
  // conversation reset or unmount without being cancelled by intra-animation
  // re-renders (which would happen if we used the effect's own cleanup).
  const animationTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const [showLanding, setShowLanding] = useState(true);
  const [isLogoAnimating, setIsLogoAnimating] = useState(false);
  const [logoAnimationPhase, setLogoAnimationPhase] = useState<
    'center' | 'moving-up' | 'moving-left' | 'header'
  >('center');
  const [logoTranslation, setLogoTranslation] = useState({ x: 0, y: 0 });
  const [slideY, setSlideY] = useState(0);

  // chatOpacity: chat content mounts at 0 and fades to 1.
  // This is set immediately when showLanding flips so the 0.5s CSS transition
  // takes the content from invisible to visible — no mount blink.
  const [chatOpacity, setChatOpacity] = useState(0);

  useEffect(() => {
    if (showLanding) {
      setChatOpacity(0);
    } else {
      setChatOpacity(1);
    }
  }, [showLanding]);

  useEffect(() => {
    document.body.classList.add('deepsearch-page');
    return () => {
      document.body.classList.remove('deepsearch-page');
    };
  }, []);

  useEffect(() => {
    const justLoaded = prevIsLoadingSession.current && !isLoadingSession;
    prevIsLoadingSession.current = isLoadingSession;
    if (!isLoadingSession) {
      if (justLoaded) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      } else {
        lastMessageTopRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [messages, isLoading, isLoadingSession]);

  const hasMessages = messages.length > 0 && !isLoadingSession;

  useEffect(() => {
    if (!hasMessages) {
      setAtTop(false);
      setAtBottom(false);
      return;
    }
    const topEl = messagesTopRef.current;
    const bottomEl = messagesEndRef.current;
    if (!topEl || !bottomEl) {
      return;
    }

    const root = messagesAreaRef.current;
    const topObserver = new IntersectionObserver(
      ([e]) => setAtTop(e.isIntersecting),
      { root }
    );
    const bottomObserver = new IntersectionObserver(
      ([e]) => setAtBottom(e.isIntersecting),
      { root }
    );
    topObserver.observe(topEl);
    bottomObserver.observe(bottomEl);

    return () => {
      topObserver.disconnect();
      bottomObserver.disconnect();
    };
  }, [hasMessages]);

  useEffect(() => {
    if (messages.length === 0 && !isLoadingSession) {
      animationTimersRef.current.forEach(clearTimeout);
      animationTimersRef.current = [];
      setShowLanding(true);
      setIsLogoAnimating(false);
      setLogoAnimationPhase('center');
      setLogoTranslation({ x: 0, y: 0 });
      setSlideY(0);
    }
  }, [messages.length, isLoadingSession]);

  useEffect(() => {
    if (!showLanding || isLogoAnimating) {
      return;
    }

    if (isLoadingSession) {
      // Session fetch in flight — reveal chat frame immediately, spinner shows inside
      setShowLanding(false);
      return;
    }

    if (messages.length === 0) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role !== 'user') {
      // Resumed conversation already fully loaded — reveal instantly
      setShowLanding(false);
      return;
    }

    // New user message — play the logo animation before revealing chat
    const logos = document.querySelectorAll('img[alt="brand logo"]');
    const sidebarLogoEl = logos[0] as HTMLElement | undefined;
    const landingLogoEl = logos[1] as HTMLElement | undefined;
    if (sidebarLogoEl && landingLogoEl) {
      const sr = sidebarLogoEl.getBoundingClientRect();
      const lr = landingLogoEl.getBoundingClientRect();
      setLogoTranslation({
        x: sr.left + sr.width / 2 - (lr.left + lr.width / 2),
        y: sr.top + sr.height / 2 - (lr.top + lr.height / 2),
      });
    }

    const landingInputEl = pageContainerRef.current?.querySelector(
      '[data-role="landing-chat-input"]'
    ) as HTMLElement | null;
    if (landingInputEl) {
      const lr = landingInputEl.getBoundingClientRect();
      setSlideY(window.innerHeight - lr.top + 20);
    }

    setIsLogoAnimating(true);
    setLogoAnimationPhase('moving-up');

    animationTimersRef.current.push(
      setTimeout(() => {
        setLogoAnimationPhase('moving-left');
      }, 700),
      setTimeout(() => {
        setLogoAnimationPhase('header');
        setShowLanding(false);
        setIsLogoAnimating(false);
      }, 1300)
    );
  }, [
    messages.length,
    showLanding,
    isLogoAnimating,
    messages,
    isLoadingSession,
  ]);

  // Auto-select conversation from URL on page load / reload
  useEffect(() => {
    if (!contextId || activeConversationId === contextId) {
      return;
    }
    const exists = conversations.some((c) => c.id === contextId);
    if (exists) {
      selectConversation(contextId);
    }
  }, [contextId, conversations, activeConversationId, selectConversation]);

  // Clear any pending animation timers on unmount.
  useEffect(() => {
    return () => {
      animationTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  const userMessages = messages.filter((m) => m.role === 'user');

  // Keep a ref so the scroll handler always reads the latest list without
  // needing to be re-registered when messages change.
  const userMessagesRef = useRef(userMessages);
  userMessagesRef.current = userMessages;

  // A message is "at the top" (i.e., the current anchor) when its top edge is
  // within NAV_BUFFER px of the area's top edge. Messages clearly above that
  // zone are reachable by prev; messages clearly below are reachable by next.
  const NAV_BUFFER = 60;

  const computeNavState = () => {
    const area = messagesAreaRef.current;
    if (!area) {
      return { prev: false, next: false };
    }
    const areaTop = area.getBoundingClientRect().top;
    let prev = false;
    let next = false;
    for (const msg of userMessagesRef.current) {
      const el = userMsgRefsMap.current.get(msg.id);
      if (!el) {
        continue;
      }
      const top = el.getBoundingClientRect().top;
      if (top < areaTop - NAV_BUFFER) {
        prev = true;
      }
      if (top > areaTop + NAV_BUFFER) {
        next = true;
      }
      if (prev && next) {
        break;
      }
    }
    return { prev, next };
  };

  // rAF-throttled scroll listener keeps canGoPrev/canGoNext in sync.
  useEffect(() => {
    const area = messagesAreaRef.current;
    if (!area || !hasMessages) {
      return;
    }
    let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
    const handler = () => {
      if (rafId !== null) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const { prev, next } = computeNavState();
        setCanGoPrev(prev);
        setCanGoNext(next);
      });
    };
    area.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => {
      area.removeEventListener('scroll', handler);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [hasMessages]);

  // Re-seed when the message list grows (new messages may shift the layout).
  useEffect(() => {
    if (hasMessages) {
      requestAnimationFrame(() => {
        const { prev, next } = computeNavState();
        setCanGoPrev(prev);
        setCanGoNext(next);
      });
    }
  }, [messages.length, hasMessages]);

  // Scroll to the closest user message that is clearly above the anchor zone.
  const handleNavUp = () => {
    const area = messagesAreaRef.current;
    if (!area) {
      return;
    }
    const areaTop = area.getBoundingClientRect().top;
    for (let i = userMessages.length - 1; i >= 0; i--) {
      const el = userMsgRefsMap.current.get(userMessages[i].id);
      if (el && el.getBoundingClientRect().top < areaTop - NAV_BUFFER) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
  };

  // Scroll to the closest user message that is clearly below the anchor zone.
  const handleNavDown = () => {
    const area = messagesAreaRef.current;
    if (!area) {
      return;
    }
    const areaTop = area.getBoundingClientRect().top;
    for (const msg of userMessages) {
      const el = userMsgRefsMap.current.get(msg.id);
      if (el && el.getBoundingClientRect().top > areaTop + NAV_BUFFER) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
  };

  const handleChipClick = (type: string) => {
    const item = PLACEHOLDER_COLLECTION.find((p) => p.searchType === type);
    if (item) {
      chatInputRef.current?.setValue(item.chipMainText, item.chipExampleText);
    }
  };

  return (
    <PageContainer ref={pageContainerRef}>
      {(messages.length > 0 || isLoadingSession) && (
        <MessagesArea ref={messagesAreaRef}>
          {isLoadingSession ? (
            <CircularProgress
              sx={{ display: 'block', margin: '64px auto 0' }}
            />
          ) : (
            <>
              <div ref={messagesTopRef} />
              {(() => {
                const lastUserIndex = messages.reduce(
                  (acc, msg, i) => (msg.role === 'user' ? i : acc),
                  -1
                );
                const anchorIndex =
                  lastUserIndex >= 0 ? lastUserIndex : messages.length - 1;
                return messages.map((message, index) => {
                  const isAnchor = index === anchorIndex;
                  if (Array.isArray(message.data)) {
                    return (
                      <Fragment key={message.id}>
                        {isAnchor && <div ref={lastMessageTopRef} />}
                        <A2uiReportRenderer messages={message.data} />
                      </Fragment>
                    );
                  }
                  return (
                    <Fragment key={message.id}>
                      {isAnchor && <div ref={lastMessageTopRef} />}
                      <MessageBubble
                        isUser={message.role === 'user'}
                        ref={
                          message.role === 'user'
                            ? (el: HTMLDivElement | null) => {
                                if (el) {
                                  userMsgRefsMap.current.set(message.id, el);
                                } else {
                                  userMsgRefsMap.current.delete(message.id);
                                }
                              }
                            : undefined
                        }
                      >
                        {message.role === 'assistant' ? (
                          <MarkdownMessage content={message.data} />
                        ) : (
                          <>
                            {message.attachment && (
                              <Box sx={{ mb: message.data ? 1 : 0 }}>
                                <FilePreview
                                  kind={message.attachment.kind}
                                  previewUrl={message.attachment.previewUrl}
                                  fileName={message.attachment.fileName}
                                />
                              </Box>
                            )}
                            {message.data}
                          </>
                        )}
                      </MessageBubble>
                    </Fragment>
                  );
                });
              })()}
              {isLoading && (
                <ThinkingStepsIndicator>
                  <CircularProgress
                    size={18}
                    color="inherit"
                    aria-label="Thinking..."
                  />
                  <ShimmerText variant="body2" fontSize="1rem">
                    {thinkingStep || 'Thinking...'}
                  </ShimmerText>
                </ThinkingStepsIndicator>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </MessagesArea>
      )}

      {!showLanding && (
        <ChatViewInputWrapper chatOpacity={chatOpacity}>
          {userMessages.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                maxWidth: '800px',
                mb: 0.5,
                px: 2,
                gap: 0.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  messagesTopRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
                disabled={atTop}
                aria-label="Scroll to top"
              >
                <KeyboardDoubleArrowUp fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleNavUp}
                disabled={!canGoPrev}
                aria-label="Scroll to previous message"
              >
                <KeyboardArrowUp fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleNavDown}
                disabled={!canGoNext}
                aria-label="Scroll to next message"
              >
                <KeyboardArrowDown fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  messagesEndRef.current?.scrollIntoView({
                    behavior: 'smooth',
                  })
                }
                disabled={atBottom}
                aria-label="Scroll to bottom"
              >
                <KeyboardDoubleArrowDown fontSize="small" />
              </IconButton>
            </Box>
          )}
          <DeepSearchChatInput initiallyHidden />
        </ChatViewInputWrapper>
      )}

      {showLanding && (
        <DeepSearchLandingPageContainer isTransitioning={isLogoAnimating}>
          <LandingContentOuterStack>
            <LandingContentInnerStack>
              <LogoTextGroupStack>
                <BrandLogoChat
                  width={isMobile ? '100px' : '138px'}
                  height={isMobile ? '16px' : '21px'}
                  sx={buildLogoAnimationSx(
                    isLogoAnimating,
                    logoAnimationPhase,
                    logoTranslation.x,
                    logoTranslation.y
                  )}
                />
                <FadeOutBox width="100%" isAnimating={isLogoAnimating}>
                  <TitleRow textAlign={isMobile ? 'left' : 'center'}>
                    <TitlePhrase>Ask More.</TitlePhrase>{' '}
                    <TitlePhrase>
                      Discover <TitleHighlight>More</TitleHighlight>
                    </TitlePhrase>
                  </TitleRow>
                </FadeOutBox>
                <FadeOutBox
                  width="100%"
                  isAnimating={isLogoAnimating}
                  mb={isMobile ? 4.5 : 6}
                >
                  <Text
                    variant="body1"
                    textAlign={isMobile ? 'left' : 'center'}
                  >
                    Your AI assistant for in-depth searches on people, phones,
                    addresses, emails, and vehicles.
                  </Text>
                </FadeOutBox>
              </LogoTextGroupStack>

              <SlideOutInputWrapper
                isAnimating={isLogoAnimating}
                slideY={slideY}
              >
                <DeepSearchChatInput ref={chatInputRef} isLanding />
              </SlideOutInputWrapper>

              <SlideOutChipsStack isAnimating={isLogoAnimating} slideY={slideY}>
                <ChatChips
                  chips={CHIPS}
                  onChipClick={handleChipClick}
                  lightBlueHover
                />
              </SlideOutChipsStack>
            </LandingContentInnerStack>

            <SlideOutTermsStack isAnimating={isLogoAnimating} slideY={slideY}>
              <ChatTerms isLanding />
            </SlideOutTermsStack>
          </LandingContentOuterStack>
        </DeepSearchLandingPageContainer>
      )}
    </PageContainer>
  );
};
